import { say } from "../../words.ts";

/**
 * The page's own refusal of an act while a Game Master has frozen its player (ADR-056's amendment of 2026-10-06,
 * decision 42). The gateway drops a frozen account's acts unanswered, so a page that started a swing for each press
 * would play swings the server never judged; every input layer that would send a message the freeze drops asks this
 * gate first and starts nothing when it refuses. It is a courtesy to the player: the server's own drop is the rule,
 * and nothing here is trusted by it.
 *
 * @example
 * const gate = new FrozenGate({ frozen: moderation.frozen, say: (text) => messageLine.say(text), now: () => performance.now() });
 * if (gate.refuses()) {
 *   return false;
 * }
 */

/**
 * The least time between two showings of the line, in ms, however many inputs are refused in it.
 */
export const FROZEN_LINE_EVERY_MS = 2000;

/**
 * What the gate reads and where it speaks: whether a freeze stands now, whether the own character reads held, the
 * message line, and the page's clock in ms.
 */
export interface FrozenGateOptions {
  readonly frozen: () => boolean;
  /**
   * Whether the page's own character reads held, as the snapshot's away state says (`Away.Held`): true through a
   * freeze the map server holds and through the grace after its release, in which the character may walk and nothing
   * else (ADR-056's amendment "a frozen character is safe", decision 41). Absent, the gate refuses by the freeze alone.
   */
  readonly held?: () => boolean;
  readonly say: (text: string) => void;
  readonly now: () => number;
}

/**
 * The gate.
 */
export class FrozenGate {
  private saidAt = Number.NEGATIVE_INFINITY;

  /**
   * @param options The freeze, the message line and the clock.
   */
  public constructor(private readonly options: FrozenGateOptions) {}

  /**
   * Whether a freeze stands now, for a reader that refuses nothing by it and wants no line said, such as the drop of
   * a press held when the freeze arrived. Works unbound.
   *
   * @returns True while the player is frozen, and through the grace after it, while the own character reads held.
   */
  public readonly frozen = (): boolean => this.options.frozen() || this.options.held?.() === true;

  /**
   * Asks whether an input the player just made is refused: while a freeze stands it is, and the line is said, with
   * no sound, unless it was said less than `FROZEN_LINE_EVERY_MS` ago; in the grace after the release, while the own
   * character still reads held, it is refused too, with the fight's line for a held character. Works unbound.
   *
   * @returns True when the input must start and send nothing.
   */
  public readonly refuses = (): boolean => {
    const frozen = this.options.frozen();
    if (!frozen && this.options.held?.() !== true) {
      return false;
    }
    const now = this.options.now();
    if (now - this.saidAt >= FROZEN_LINE_EVERY_MS) {
      this.saidAt = now;
      this.options.say(say(frozen ? "moderation.notice.frozenAct" : "fight.refused.held"));
    }
    return true;
  };
}
